import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-[#1a2e4a] py-8 px-4 md:px-6" data-id="element-44">
        <div className="max-w-3xl mx-auto flex flex-col items-center gap-4" data-id="element-45">
          <span className="font-serif text-lg text-[#faf6f0] font-bold tracking-wide" data-id="element-46">
            Pimpam Creations
          </span>
          <div className="flex items-center gap-6" data-id="element-47">
            <button onClick={() => setActiveModal('privacy')} className="text-[#faf6f0]/60 text-sm hover:text-[#c4956a] transition-colors" data-id="element-48">
              Política de Privacidad
            </button>
            <span className="text-[#faf6f0]/20" data-id="element-49">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-[#faf6f0]/60 text-sm hover:text-[#c4956a] transition-colors" data-id="element-50">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-[#faf6f0]/40 text-xs mt-2" data-id="element-51">
            © {new Date().getFullYear()} Pimpam Creations. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-52">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-53" />}
      </AnimatePresence>
    </>;
}
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-54">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#1a2e4a]/70 backdrop-blur-sm" data-id="element-55" />

      {/* Modal */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} onClick={e => e.stopPropagation()} className="relative bg-[#faf6f0] rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden" data-id="element-56">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#d4a0a0]/20 bg-white" data-id="element-57">
          <h2 className="font-serif text-xl font-bold text-[#1a2e4a]" data-id="element-58">
            {title}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[#1a2e4a]/10 transition-colors text-[#5a5a5a]" data-id="element-59">
            <X size={20} data-id="element-60" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto px-6 py-6 text-[#5a5a5a] text-sm leading-relaxed space-y-4" data-id="element-61">
          {content.map((section, i) => <div key={i} data-id="element-62">
              {section.heading && <h3 className="font-serif text-base font-bold text-[#1a2e4a] mb-2 mt-4" data-id="element-63">
                  {section.heading}
                </h3>}
              <p data-id="element-64">{section.text}</p>
            </div>)}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#d4a0a0]/20 bg-white text-center" data-id="element-65">
          <button onClick={onClose} className="text-sm font-medium text-[#c4956a] hover:text-[#b0845c] transition-colors" data-id="element-66">
            Cerrar
          </button>
        </div>
      </motion.div>
    </motion.div>;
}
const privacyContent = [{
  heading: null,
  text: 'Última actualización: febrero 2026'
}, {
  heading: '1. Responsable del tratamiento',
  text: 'Pimpam Creations es responsable del tratamiento de los datos personales recogidos a través de este sitio web.'
}, {
  heading: '2. Datos que recopilamos',
  text: 'Recopilamos únicamente los datos necesarios para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos datos de tarjetas de crédito directamente; estos son gestionados por nuestro procesador de pagos seguro.'
}, {
  heading: '3. Finalidad del tratamiento',
  text: 'Tus datos se utilizan exclusivamente para: gestionar tu pedido y enviarte el producto digital adquirido, enviarte comunicaciones relacionadas con tu compra, y cumplir con obligaciones legales.'
}, {
  heading: '4. Base legal',
  text: 'El tratamiento de tus datos se basa en la ejecución del contrato de compra y, en su caso, en tu consentimiento expreso para comunicaciones comerciales.'
}, {
  heading: '5. Conservación de datos',
  text: 'Tus datos se conservarán durante el tiempo necesario para cumplir con la finalidad para la que fueron recogidos y para cumplir con las obligaciones legales aplicables.'
}, {
  heading: '6. Derechos',
  text: 'Puedes ejercer tus derechos de acceso, rectificación, supresión, oposición, limitación del tratamiento y portabilidad escribiéndonos a nuestro correo electrónico.'
}, {
  heading: '7. Cookies',
  text: 'Este sitio web puede utilizar cookies técnicas y analíticas. No utilizamos cookies publicitarias ni de seguimiento de terceros.'
}];
const termsContent = [{
  heading: null,
  text: 'Última actualización: febrero 2026'
}, {
  heading: '1. Identificación',
  text: 'Este sitio web es propiedad de Pimpam Creations. Al realizar una compra, aceptas los presentes términos y condiciones.'
}, {
  heading: '2. Producto',
  text: 'El producto «Tu bebé tarde‑noche» es un kit digital (PDF) con contenido informativo y educativo. No constituye asesoramiento médico ni sustituye la consulta con un profesional de la salud.'
}, {
  heading: '3. Precio y pago',
  text: 'El precio del producto es el indicado en la página de venta en el momento de la compra, impuestos incluidos. El pago se realiza de forma segura a través de nuestro procesador de pagos.'
}, {
  heading: '4. Entrega',
  text: 'Al completar la compra, recibirás acceso inmediato al producto digital a través de un enlace de descarga enviado a tu correo electrónico.'
}, {
  heading: '5. Política de devoluciones',
  text: 'Dado que se trata de un producto digital de acceso inmediato, no se admiten devoluciones una vez descargado el contenido, salvo en los casos previstos por la legislación vigente.'
}, {
  heading: '6. Propiedad intelectual',
  text: 'Todo el contenido del kit (textos, diseños, ilustraciones) es propiedad de Pimpam Creations. Queda prohibida su reproducción, distribución o comunicación pública sin autorización expresa.'
}, {
  heading: '7. Limitación de responsabilidad',
  text: 'Pimpam Creations no se hace responsable de los resultados individuales obtenidos con el uso del producto. Cada bebé y cada situación familiar es diferente. Si tu bebé presenta señales de alarma, consulta siempre con tu pediatra.'
}, {
  heading: '8. Legislación aplicable',
  text: 'Estos términos se rigen por la legislación española. Para cualquier controversia, las partes se someterán a los juzgados y tribunales competentes.'
}];